'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ChangeEvent, FormEvent } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select-field';
interface MoveFundsInputStepProps {
  selectedWalletId: string;
  sourceItems: {
    value: string;
    label: string;
  }[];
  onSourceChange: (walletId: string) => void;
  destinationWalletId: string;
  destinationItems: {
    value: string;
    label: string;
  }[];
  onDestinationChange: (walletId: string) => void;
  amount: string;
  onAmountChange: (value: string) => void;
  inputError: string | null;
  /** Pre-built "available balance" caption (USDC + formatted-currency). */
  availableBalanceLabel: string;
  onMaxAmount: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

/**
 * Step 1 of the move-between-wallets wizard: pick a source + destination
 * wallet and an amount. The `move-funds-form` id is shared with the footer's
 * submit button (rendered by the parent SilkSheet `footer`). Purely
 * presentational — all state, validation, and submission live in the parent
 * `MoveFunds`.
 */
export function MoveFundsInputStep(t0) {
  const $ = _c(50);
  const {
    selectedWalletId,
    sourceItems,
    onSourceChange,
    destinationWalletId,
    destinationItems,
    onDestinationChange,
    amount,
    onAmountChange,
    inputError,
    availableBalanceLabel,
    onMaxAmount,
    onSubmit
  } = t0;
  const t = useTranslations("wallet.move");
  let t1;
  if ($[0] !== t) {
    t1 = t("from_label");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSourceChange || $[3] !== selectedWalletId || $[4] !== sourceItems || $[5] !== t1) {
    t2 = <SelectField id="move-from" label={t1} value={selectedWalletId} onValueChange={onSourceChange} items={sourceItems} />;
    $[2] = onSourceChange;
    $[3] = selectedWalletId;
    $[4] = sourceItems;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("to_label");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== destinationItems || $[10] !== destinationWalletId || $[11] !== onDestinationChange || $[12] !== t3) {
    t4 = <SelectField id="move-to" label={t3} value={destinationWalletId} onValueChange={onDestinationChange} items={destinationItems} />;
    $[9] = destinationItems;
    $[10] = destinationWalletId;
    $[11] = onDestinationChange;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t) {
    t5 = t("amount_label");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <Label htmlFor="move-amount">{t5}</Label>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== onAmountChange) {
    t7 = e => {
      onAmountChange(e.target.value);
    };
    $[18] = onAmountChange;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("amount_placeholder");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = inputError !== null ? true : undefined;
  const t10 = inputError !== null ? "move-error" : undefined;
  let t11;
  if ($[22] !== amount || $[23] !== t10 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t11 = <InputField id="move-amount" type="number" value={amount} onChange={t7} placeholder={t8} min={0} step="any" autoComplete="off" aria-invalid={t9} aria-describedby={t10} />;
    $[22] = amount;
    $[23] = t10;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("max_button");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== onMaxAmount || $[31] !== t12) {
    t13 = <AnimatedButton type="button" variant="secondary" onClick={onMaxAmount} className="h-10 shrink-0">{t12}</AnimatedButton>;
    $[30] = onMaxAmount;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t13) {
    t14 = <div className="flex gap-2">{t11}{t13}</div>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== availableBalanceLabel) {
    t15 = <p className="text-caption text-foreground-secondary">{availableBalanceLabel}</p>;
    $[36] = availableBalanceLabel;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t6) {
    t16 = <div className="flex flex-col gap-1.5">{t6}{t14}{t15}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t6;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== inputError) {
    t17 = inputError !== null && <p id="move-error" role="alert" className="text-body text-feedback-error-content">{inputError}</p>;
    $[42] = inputError;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== onSubmit || $[45] !== t16 || $[46] !== t17 || $[47] !== t2 || $[48] !== t4) {
    t18 = <form id="move-funds-form" onSubmit={onSubmit} className="flex flex-col gap-4">{t2}{t4}{t16}{t17}</form>;
    $[44] = onSubmit;
    $[45] = t16;
    $[46] = t17;
    $[47] = t2;
    $[48] = t4;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
}